import styled, { keyframes } from 'styled-components';

export const StateWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: ${({ theme: { spacing } }) =>
    `${spacing?.level10 ?? '64px'} ${spacing?.level6 ?? '24px'}`};
  gap: ${({ theme: { spacing } }) => spacing?.level5 ?? '16px'};
  color: ${({ theme: { colors = {} } }) => colors.onSurfaceVariant ?? '#C7C4D7'};
`;

export const StateTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  margin: 0;
  color: ${({ theme: { colors = {} } }) => colors.onSurface ?? '#E4E1ED'};
`;

export const StateBody = styled.p`
  margin: 0;
  font-size: 14px;
  max-width: 420px;
  line-height: 1.5;
`;

export const StateActions = styled.div`
  display: flex;
  gap: ${({ theme: { spacing } }) => spacing?.level3 ?? '8px'};
  margin-top: ${({ theme: { spacing } }) => spacing?.level3 ?? '8px'};
`;

const shimmer = keyframes`
  0% { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;

export const SkeletonBlock = styled.div<{
  $width?: string;
  $height?: string;
  $radius?: string;
}>`
  display: inline-block;
  width: ${({ $width = '100%' }) => $width};
  height: ${({ $height = '14px' }) => $height};
  border-radius: ${({ $radius = '4px' }) => $radius};
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.04) 0%,
    rgba(255, 255, 255, 0.1) 50%,
    rgba(255, 255, 255, 0.04) 100%
  );
  background-size: 800px 100%;
  animation: ${shimmer} 1.4s linear infinite;
  vertical-align: middle;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

/**
 * Skeleton placeholder for a single process row. Mirrors `ProcessesList`'s
 * card layout: padded surface-container-low rectangle, 40px icon block on
 * the left, two stacked title/meta lines, and a small actions cluster on the
 * right. NO dividers — the gap on `SkeletonList` does the row separation.
 */
export const SkeletonRow = styled.div`
  display: grid;
  grid-template-columns: 40px 1fr auto;
  gap: ${({ theme: { spacing } }) => spacing?.level5 ?? '16px'};
  align-items: center;
  padding: 12px 16px;
  border-radius: ${({ theme: { borderRadius = {} } }) =>
    borderRadius.large ?? '10px'};
  background: ${({ theme: { colors = {} } }) =>
    `color-mix(in oklch, ${colors.onSurface ?? '#E4E1ED'} 4%, transparent)`};
`;

export const SkeletonList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const SkeletonNameStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`;

export const SkeletonActionsEnd = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

/**
 * Footer slot under the infinite-scroll list. Doubles as the
 * IntersectionObserver sentinel — when this scrolls into view, the next
 * page is fetched. Always rendered so the observer's target survives across
 * loading transitions; visibility of the inner spinner / end-of-list label
 * is controlled by `data-state`.
 */
export const ScrollFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 40px;
  padding: ${({ theme: { spacing } }) =>
    `${spacing?.level4 ?? '12px'} ${spacing?.level5 ?? '16px'}`};
  color: ${({ theme: { colors = {} } }) => colors.onSurfaceVariant ?? '#C7C4D7'};
  font-size: 12px;
`;

export const ScrollFooterSpinner = styled.span`
  display: inline-block;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2px solid
    ${({ theme: { colors = {} } }) =>
      `color-mix(in oklch, ${colors.onSurface ?? '#E4E1ED'} 18%, transparent)`};
  border-top-color: ${({ theme: { colors = {} } }) =>
    colors.primary ?? '#C1C1FF'};
  margin-right: 8px;
  animation: ${spin} 720ms linear infinite;
  vertical-align: middle;

  /* Honour the OS preference — slowing the spinner is still motion. Stop it
   * outright and lean on the surrounding "Loading more processes…" text +
   * aria-live region in ProcessesPage to convey progress instead. */
  @media (prefers-reduced-motion: reduce) {
    animation: none;
    transition: none;
  }
`;
